<template>
  <div>
    <!-- Main Price Books Modal -->
    <v2-modal
      :show="show && !showOrderSelectionStep"
      title="Price Books"
      size="lg"
      @close="onClickClosePriceBook"
    >
      <div
        class="v2-price-books"
        :class="{ 'v2-price-books--disabled': isExportingPriceBook }"
      >
        <template v-if="!isImportingPriceBook">
          <!-- Create Button -->
          <div class="v2-flex v2-justify-end v2-mb-4">
            <v2-button
              v-if="!showCreatePriceBook"
              size="sm"
              @click="showCreatePriceBook = true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="12"
                  y1="5"
                  x2="12"
                  y2="19"
                />
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12"
                />
              </svg>
              Create
            </v2-button>
          </div>

          <!-- Create Price Book Form -->
          <div
            v-if="showCreatePriceBook"
            class="v2-price-books__create-form"
          >
            <v2-input
              :value="priceBookName"
              label="Enter price book name"
              placeholder="Enter price book name"
              @input="priceBookName = $event"
            />

            <!-- Currency dropdown -->
            <div
              v-if="hasTransactionCurrencyFeature"
              class="v2-mt-3"
            >
              <p class="v2-text-xs v2-text-muted v2-mb-2">
                * If no currency is selected, collection's base currency will be used
              </p>
              <div class="v2-price-books__currency-select">
                <div
                  class="v2-price-books__currency-trigger"
                  @click="showCurrencyDropdown = !showCurrencyDropdown"
                >
                  <span :class="{ 'v2-text-muted': !selectedCurrency }">
                    {{ selectedCurrency || 'Select currency' }}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <div
                  v-if="showCurrencyDropdown"
                  class="v2-price-books__currency-dropdown"
                >
                  <div class="v2-price-books__currency-search">
                    <v2-input
                      placeholder="Search"
                      @input="searchCurrency"
                    />
                  </div>
                  <div
                    v-if="!currencies.length"
                    class="v2-price-books__currency-empty"
                  >
                    Nothing was found. Try another currency.
                  </div>
                  <div class="v2-price-books__currency-list">
                    <div
                      v-for="name in currencies"
                      :key="name"
                      class="v2-price-books__currency-item"
                      :class="{ 'v2-price-books__currency-item--active': selectedCurrency === name }"
                      @click="onSelectCurrency(name)"
                    >
                      {{ name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="v2-flex v2-justify-end v2-gap-2 v2-mt-3">
              <v2-button
                variant="outline"
                size="sm"
                @click="cancelCreatePriceBook"
              >
                Cancel
              </v2-button>
              <v2-button
                size="sm"
                :disabled="isCreating || !priceBookName"
                :loading="isCreating"
                @click="createPriceBook"
              >
                {{ isCreating ? 'Creating' : 'Create' }}
              </v2-button>
            </div>
          </div>

          <!-- Master Price Book -->
          <div class="v2-mt-4">
            <div class="v2-price-books__section-title">
              Master Price Book
            </div>
            <div class="v2-price-books__item">
              <div class="v2-price-books__item-name">
                Master Price Book
              </div>
              <div class="v2-flex v2-gap-2">
                <v2-button
                  variant="outline"
                  size="sm"
                  :disabled="isExportingPriceBook && !exportingPriceBookId"
                  :loading="isExportingPriceBook && !exportingPriceBookId"
                  @click="exportMasterPriceBook"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                    />
                  </svg>
                  Export
                </v2-button>
                <v2-button
                  variant="outline"
                  size="sm"
                  @click="onClickImportPriceBook()"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  Import
                </v2-button>
              </div>
            </div>
          </div>

          <!-- Price Book List -->
          <div
            v-if="priceBookList.length"
            class="v2-mt-4"
          >
            <div class="v2-price-books__section-title">
              All Price Books ({{ priceBookList.length }})
            </div>
            <div class="v2-price-books__list">
              <div
                v-for="priceBook in priceBookList"
                :key="priceBook._id"
                class="v2-price-books__item"
              >
                <div class="v2-price-books__item-info">
                  <div class="v2-flex v2-items-center v2-gap-2">
                    <template v-if="!priceBook.isEditing">
                      <span
                        class="v2-price-books__item-name v2-text-truncate"
                        :title="getPriceBookNameWithoutCurrency(priceBook.priceBookName)"
                      >
                        {{ getPriceBookNameWithoutCurrency(priceBook.priceBookName) }}
                      </span>
                      <span
                        v-if="getPriceBookCurrency(priceBook.priceBookName)"
                        class="v2-text-xs v2-text-muted"
                      >
                        ({{ getPriceBookCurrency(priceBook.priceBookName) }})
                      </span>
                    </template>
                    <template v-else>
                      <div class="v2-price-books__rename-box">
                        <v2-input
                          :value="priceBook.newName"
                          :error="getNameError(priceBook.newName)"
                          @input="handleNameInput($event, priceBook)"
                          @keyup.native.esc="cancelRename(priceBook)"
                        />
                        <div class="v2-price-books__rename-actions">
                          <button
                            v-if="priceBook.newName && validateName(priceBook.newName) && !isRenaming"
                            class="v2-price-books__rename-action"
                            @click="renamePriceBook(priceBook)"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="var(--v2-success)"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </button>
                          <button
                            v-if="!isRenaming"
                            class="v2-price-books__rename-action"
                            @click="cancelRename(priceBook)"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="var(--v2-destructive)"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <line
                                x1="18"
                                y1="6"
                                x2="6"
                                y2="18"
                              />
                              <line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Edit / Delete buttons (when transaction currency feature is on) -->
                  <div
                    v-if="hasTransactionCurrencyFeature && !priceBook.isEditing"
                    class="v2-flex v2-items-center v2-gap-2"
                  >
                    <button
                      class="v2-price-books__icon-btn"
                      @click="startRenamePricebook(priceBook)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      class="v2-price-books__icon-btn v2-price-books__icon-btn--danger"
                      @click="deletePricebook(priceBook)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="v2-flex v2-gap-2">
                  <v2-button
                    variant="outline"
                    size="sm"
                    :disabled="isExportingPriceBook && exportingPriceBookId === priceBook._id"
                    :loading="isExportingPriceBook && exportingPriceBookId === priceBook._id"
                    @click="exportPriceBook(priceBook)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line
                        x1="12"
                        y1="3"
                        x2="12"
                        y2="15"
                      />
                    </svg>
                    Export
                  </v2-button>
                  <v2-button
                    variant="outline"
                    size="sm"
                    @click="onClickImportPriceBook(priceBook)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line
                        x1="12"
                        y1="15"
                        x2="12"
                        y2="3"
                      />
                    </svg>
                    Import
                  </v2-button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Import UI -->
        <template v-else>
          <div
            @dragover.prevent
            @drop.prevent
          >
            <p class="v2-text-sm v2-mb-4">
              Upload file for
              {{ selectedPriceBook ? selectedPriceBook.priceBookName : 'Master Price Book' }}
            </p>

            <!-- Drag & Drop -->
            <div
              v-if="!priceBookFile"
              class="v2-price-books__dropzone"
              :class="{ 'v2-price-books__dropzone--dragging': isDragging }"
              @click="$refs.uploadPricebookFile.click()"
              @drop="onDropPriceBookFile"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="v2-price-books__upload-icon"
              >
                <polyline points="16 16 12 12 8 16" />
                <line
                  x1="12"
                  y1="12"
                  x2="12"
                  y2="21"
                />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                <polyline points="16 16 12 12 8 16" />
              </svg>
              <p class="v2-font-semibold v2-mt-2">
                Drag & Drop
              </p>
              <p class="v2-text-sm v2-text-muted">
                or click to select files from your system
              </p>
            </div>

            <!-- File selected -->
            <div
              v-else
              class="v2-price-books__file"
            >
              <div class="v2-flex v2-items-center v2-gap-3">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-price-books__file-icon"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <div>
                  <p class="v2-text-sm v2-font-medium">
                    {{ priceBookFile.name }}
                  </p>
                  <p class="v2-text-xs v2-text-muted">
                    {{ getSizeInKb(priceBookFile.size) }} KB
                  </p>
                </div>
              </div>
              <button
                class="v2-price-books__remove-btn"
                @click="priceBookFile = null"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>

            <input
              ref="uploadPricebookFile"
              type="file"
              accept=".xlsx"
              hidden
              @change="onPriceBookFileSelect"
            >
          </div>

          <div class="v2-flex v2-justify-center v2-gap-3 v2-mt-6">
            <v2-button
              variant="outline"
              :disabled="isUploading"
              @click="onClickBackPriceBook"
            >
              Back
            </v2-button>
            <v2-button
              :disabled="isUploading || !priceBookFile"
              :loading="isUploading"
              @click="importPriceBook"
            >
              {{ isUploading ? 'Uploading...' : 'Upload' }}
            </v2-button>
          </div>
        </template>
      </div>
    </v2-modal>

    <!-- Order Selection Modal for Price Book Import -->
    <v2-modal
      :show="show && showOrderSelectionStep"
      title="Select Orders for Price Book Import"
      size="lg"
      @close="closePriceBookOrderModal"
    >
      <div class="v2-price-books__orders">
        <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4">
          <div class="v2-price-books__search-wrapper">
            <v2-input
              placeholder="Search order id, retailer name..."
              @input="onSearchPriceBookOrders"
            />
          </div>
          <v2-button
            size="sm"
            :variant="isSelectedPriceBookOrders ? 'outline' : 'primary'"
            @click="toggleSelectAllPriceBookOrders"
          >
            {{ isSelectedPriceBookOrders ? 'Deselect all' : 'Select all' }}
          </v2-button>
        </div>

        <!-- Header -->
        <div class="v2-price-books__orders-header">
          <div class="v2-price-books__orders-col">
            Order Id
          </div>
          <div class="v2-price-books__orders-col">
            Retailer Name
          </div>
        </div>

        <!-- Orders list -->
        <div class="v2-price-books__orders-list">
          <label
            v-for="order in priceBookOrderList"
            :key="order._id"
            class="v2-price-books__order-row"
          >
            <v2-checkbox
              :value="selectedPriceBookOrders.includes(order._id)"
              @input="togglePriceBookOrder(order._id)"
            />
            <div class="v2-price-books__orders-col">
              {{ order.orderSeqId || order.seqId || 'N/A' }}
            </div>
            <div class="v2-price-books__orders-col">
              {{ order.retailerName || 'N/A' }}
            </div>
          </label>
        </div>
      </div>

      <template #footer>
        <v2-button
          variant="outline"
          @click="closePriceBookOrderModal"
        >
          Cancel
        </v2-button>
        <v2-button
          :disabled="isUploading"
          :loading="isUploading"
          @click="confirmPriceBookImport"
        >
          {{ isUploading ? 'Importing...' : 'Import' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Delete Price Book Confirmation -->
    <v2-modal
      :show="!!pendingDeleteBook"
      title="Delete Price Book"
      size="sm"
      @close="cancelDeletePricebook"
    >
      <p class="v2-text-sm">
        Are you sure you want to delete
        <strong v-if="pendingDeleteBook">{{ getPriceBookNameWithoutCurrency(pendingDeleteBook.priceBookName) }}</strong>?
        It will be unassigned from the retailer. This action will not impact any of your existing orders.
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="isDeletingPricebook"
          @click="cancelDeletePricebook"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          :disabled="isDeletingPricebook"
          :loading="isDeletingPricebook"
          @click="confirmDeletePricebook"
        >
          Delete
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import store from '@/store'
import {
  CREATE_PRICE_BOOK,
  GET_PRICE_BOOK,
  IMPORT_PRICE_BOOK,
  DOWNLOAD_MASTER_PRICEBOOK,
  DELETE_PRICEBOOK,
  RENAME_PRICEBOOK,
} from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS, DOWNLOAD_FILE } from '@/store/modules/common.module'
import { GET_ORDERS_BY_COLLECTION_SEQ_IDS } from '@/store/modules/order.module'
import { getSizeInKb, parseErrorObject } from '@/@core/utils/utils'
import { apiToastSuccess } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants, { availableCurrencies, FILE_FORMATS, FLAG_CONSTANTS } from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import NotificationMixin from '@/views/mixins/NotificationMixin.vue'

const { FILE_TYPES } = c
const { MASTER_PRICE_BOOK } = constants.TRACKS.ACTIONS

export default {
  name: 'V2PriceBooksModal',
  components: {
    V2Modal,
    V2Button,
    V2Input,
    V2Checkbox,
  },
  mixins: [NotificationMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: () => ({}),
    },
    fetchCollections: {
      type: Function,
      default() {},
    },
  },
  data() {
    return {
      isRenaming: false,
      pendingDeleteBook: null,
      isDeletingPricebook: false,
      showCreatePriceBook: false,
      priceBookName: '',
      isCreating: false,
      priceBookList: [],
      isImportingPriceBook: false,
      selectedPriceBook: null,
      priceBookFile: null,
      isUploading: false,
      isExportingPriceBook: false,
      exportingPriceBookId: null,
      currencies: availableCurrencies || [],
      selectedCurrency: null,
      showCurrencyDropdown: false,
      isDragging: false,
      priceBookOrderList: [],
      allPriceBookOrders: [],
      selectedPriceBookOrders: [],
      uploadedPriceBookFileLink: null,
      showOrderSelectionStep: false,
      getSizeInKb,
    }
  },
  computed: {
    collectionId() {
      return this.collection._id
    },
    hasTransactionCurrencyFeature() {
      return store.state.flags[FLAG_CONSTANTS.TRANSACTION_CURRENCY]
    },
    hasImportPricebookOrderFeature() {
      return store.state.flags[FLAG_CONSTANTS.IMPORT_PRICEBOOK_ORDER]
    },
    isSelectedPriceBookOrders() {
      return !!this.selectedPriceBookOrders.length && this.selectedPriceBookOrders.length === this.allPriceBookOrders.length
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.getCollectionPriceBook()
      }
    },
    priceBookFile: {
      handler(val) {
        if (!val) return
        analytics.track(MASTER_PRICE_BOOK.BRAND_SELECTS_MASTER_PRICE_BOOK_FILE, val)
      },
      deep: true,
    },
  },
  methods: {
    // --- Price book CRUD ---
    async getCollectionPriceBook() {
      try {
        const result = await this.$store.dispatch(GET_PRICE_BOOK, this.collectionId)
        this.priceBookList = result.data?.data || []
        this.showCreatePriceBook = !this.priceBookList.length
      } catch (err) {
        // error toast handled by axios interceptor
      }
    },
    async createPriceBook() {
      this.isCreating = true
      try {
        const payload = {
          priceBookName: this.priceBookName,
          currency: this.selectedCurrency || null,
        }
        const result = await this.$store.dispatch(CREATE_PRICE_BOOK, { payload, collectionId: this.collectionId })
        this.getCollectionPriceBook()
        this.priceBookName = ''
        this.showCreatePriceBook = false
        this.showCurrencyDropdown = false
        apiToastSuccess(result.data.message || 'Price book created successfully')
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isCreating = false
      }
    },
    cancelCreatePriceBook() {
      if (this.priceBookList.length) {
        this.showCreatePriceBook = false
      } else {
        this.$emit('close')
      }
      this.selectedCurrency = null
      this.showCurrencyDropdown = false
    },

    // --- Currency helpers ---
    searchCurrency(searchString) {
      const searchRegex = new RegExp(searchString, 'i')
      this.currencies = availableCurrencies.filter(currency => searchRegex.test(currency))
    },
    onSelectCurrency(currency) {
      if (this.selectedCurrency === currency) {
        this.selectedCurrency = null
      } else {
        this.selectedCurrency = currency
      }
      this.showCurrencyDropdown = false
    },

    // --- Name helpers ---
    getPriceBookNameWithoutCurrency(name) {
      if (!name) return ''
      const match = name.match(/(.*?)\s*\([A-Z]{3}\)\s*$/)
      return match ? match[1].trim() : name.trim()
    },
    getPriceBookCurrency(name) {
      if (!name) return ''
      const match = name.match(/\(([A-Z]{3})\)\s*$/)
      return match ? match[1] : ''
    },
    extractNameAndCurrencyFromPriceBook(priceBookName) {
      const match = priceBookName.match(/(.*?)\s*\(([^)]*)\)\s*$/)
      if (match) {
        return { name: match[1].trim(), currency: match[2].trim() }
      }
      return { name: priceBookName, currency: null }
    },

    // --- Rename ---
    startRenamePricebook(book) {
      this.$set(book, 'isEditing', true)
      const { name } = this.extractNameAndCurrencyFromPriceBook(book.priceBookName)
      this.$set(book, 'newName', name)
    },
    validateName(name) {
      if (!name || name.trim().length === 0 || name.length > 25) return false
      return true
    },
    getNameError(name) {
      if (!name) return 'Price Book Name is required'
      if (name.length > 25) return 'Name must be 25 characters or less'
      return ''
    },
    handleNameInput(value, priceBook) {
      let trimmedValue = value
      if (value.length > 25) {
        trimmedValue = value.slice(0, 25)
      }
      this.$set(priceBook, 'newName', trimmedValue.trim())
    },
    cancelRename(book) {
      this.$delete(book, 'isEditing')
      this.$delete(book, 'newName')
    },
    async renamePriceBook(priceBook) {
      if (this.isRenaming) return
      this.isRenaming = true
      try {
        const payload = { name: priceBook.newName }
        const response = await this.$store.dispatch(RENAME_PRICEBOOK, { payload, priceBookId: priceBook._id })
        apiToastSuccess(response.data.message)
        const res = await this.$store.dispatch(GET_PRICE_BOOK, this.collectionId)
        this.priceBookList = res.data.data
        this.$delete(priceBook, 'isEditing')
        this.$delete(priceBook, 'newName')
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isRenaming = false
      }
    },

    // --- Delete ---
    deletePricebook(book) {
      this.pendingDeleteBook = book
    },
    cancelDeletePricebook() {
      if (this.isDeletingPricebook) return
      this.pendingDeleteBook = null
    },
    async confirmDeletePricebook() {
      if (!this.pendingDeleteBook || this.isDeletingPricebook) return
      this.isDeletingPricebook = true
      try {
        const response = await this.$store.dispatch(DELETE_PRICEBOOK, { priceBookId: this.pendingDeleteBook._id })
        apiToastSuccess(response.data.message)
        const res = await this.$store.dispatch(GET_PRICE_BOOK, this.collectionId)
        this.priceBookList = res.data.data
        this.pendingDeleteBook = null
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isDeletingPricebook = false
      }
    },

    // --- Export ---
    async exportMasterPriceBook() {
      this.isExportingPriceBook = true
      analytics.track(MASTER_PRICE_BOOK.BRAND_CLICKS_EXPORT_MASTER_PRICE_BOOK)
      const payload = {
        downloadType: 'masterPricebookExport',
        fileType: FILE_FORMATS.XLSX,
        respectiveId: this.collectionId,
      }
      try {
        const res = await this.$store.dispatch(DOWNLOAD_MASTER_PRICEBOOK, { payload })
        apiToastSuccess(res.data?.message)
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_EXPORTED_SUCCESSFULLY, { fileObject: res })
        this.notifyDownloadsNavMenu()
      } catch (err) {
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_EXPORT_FAILED, { error: parseErrorObject(err) })
      } finally {
        this.isExportingPriceBook = false
      }
    },
    async exportPriceBook(priceBook) {
      this.isExportingPriceBook = true
      this.exportingPriceBookId = priceBook._id
      const url = this.$kpParams(this.$kpEndpoint.catalog.pricebook.export, this.collectionId, priceBook._id)
      const payload = {
        url,
        fileName: `${priceBook?.priceBookName}-price-book.xlsx`,
        fileFormat: FILE_FORMATS.XLSX,
        successMessage: 'Price book exported successfully',
      }
      try {
        const res = await this.$store.dispatch(DOWNLOAD_FILE, payload)
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_EXPORTED_SUCCESSFULLY, { bookId: priceBook._id, fileObject: res })
        this.notifyDownloadsNavMenu()
      } catch (err) {
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_EXPORT_FAILED, { error: parseErrorObject(err) })
      } finally {
        this.isExportingPriceBook = false
        this.exportingPriceBookId = null
      }
    },

    // --- Import ---
    onClickImportPriceBook(priceBook = null) {
      this.selectedPriceBook = priceBook
      this.isImportingPriceBook = true
      if (!priceBook) {
        analytics.track(MASTER_PRICE_BOOK.BRAND_CLICKS_IMPORT_BTN_TO_OPEN_FILE_UPLOAD_POPUP)
      }
    },
    onClickBackPriceBook() {
      this.selectedPriceBook = null
      this.isImportingPriceBook = false
      this.priceBookFile = null
      analytics.track(MASTER_PRICE_BOOK.BRAND_CLICKS_BACK_BTN_ON_FILE_UPLOAD_POPUP)
    },
    onPriceBookFileSelect(e) {
      const file = e.target.files?.[0]
      if (file) {
        this.priceBookFile = file
      }
      e.target.value = ''
    },
    onDropPriceBookFile(event) {
      this.isDragging = false
      event.preventDefault()
      this.priceBookFile = event.dataTransfer.files[0]
    },
    async importPriceBook() {
      analytics.track(MASTER_PRICE_BOOK.BRAND_CLICKS_IMPORT_BTN_ON_FILE_UPLOAD_POPUP, this.priceBookFile)
      this.isUploading = true
      const currentSelectedPriceBook = this.selectedPriceBook

      try {
        const fileLink = await this.$store.dispatch(UPLOAD_DOCUMENTS, { fileType: FILE_TYPES.PRICE_BOOK, file: this.priceBookFile })
        this.uploadedPriceBookFileLink = fileLink

        if (this.hasImportPricebookOrderFeature) {
          await this.handleImportWithOrderSelection(fileLink, currentSelectedPriceBook)
        } else {
          await this.handleDirectImport(fileLink, currentSelectedPriceBook)
        }
      } catch (err) {
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_IMPORT_FAILED, { error: parseErrorObject(err) })
      } finally {
        this.isUploading = false
      }
    },
    preserveSelectedPriceBook(currentSelectedPriceBook) {
      if (currentSelectedPriceBook && !this.selectedPriceBook) {
        this.selectedPriceBook = currentSelectedPriceBook
      }
    },
    async handleImportWithOrderSelection(fileLink, currentSelectedPriceBook) {
      this.preserveSelectedPriceBook(currentSelectedPriceBook)
      const ordersResponse = await this.$store.dispatch(GET_ORDERS_BY_COLLECTION_SEQ_IDS, this.collection?._id)
      const orders = ordersResponse?.data?.data?.orders || []

      if (orders.length > 0) {
        this.allPriceBookOrders = orders
        this.priceBookOrderList = orders
        this.isUploading = false
        this.preserveSelectedPriceBook(currentSelectedPriceBook)
        this.showOrderSelectionStep = true
      } else {
        await this.importWithoutOrders(fileLink, currentSelectedPriceBook)
      }
    },
    async importWithoutOrders(fileLink, currentSelectedPriceBook) {
      this.preserveSelectedPriceBook(currentSelectedPriceBook)
      await this.performPriceBookImport(fileLink, [])
    },
    async handleDirectImport(fileLink, currentSelectedPriceBook) {
      this.preserveSelectedPriceBook(currentSelectedPriceBook)
      await this.performPriceBookImport(fileLink, [])
    },
    async confirmPriceBookImport() {
      this.isUploading = true
      try {
        const orderIds = this.selectedPriceBookOrders.length > 0 ? this.selectedPriceBookOrders : []
        await this.performPriceBookImport(this.uploadedPriceBookFileLink, orderIds)
        this.showOrderSelectionStep = false
      } catch (err) {
        analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_IMPORT_FAILED, { error: parseErrorObject(err) })
      } finally {
        this.isUploading = false
      }
    },
    async performPriceBookImport(fileLink, orderIds) {
      const payload = {
        priceBookFile: fileLink,
      }
      if (orderIds && orderIds.length > 0) {
        payload.orderIds = orderIds
      }

      const payloadObject = {
        collectionId: this.collection?._id,
        payload,
      }
      const priceBookId = this.selectedPriceBook?._id
      if (priceBookId) {
        payloadObject.priceBookId = priceBookId
      }

      const result = await this.$store.dispatch(IMPORT_PRICE_BOOK, payloadObject)
      apiToastSuccess(result.data.message)
      this.isImportingPriceBook = false
      this.priceBookFile = null
      this.selectedPriceBook = null
      this.uploadedPriceBookFileLink = null
      this.priceBookOrderList = []
      this.allPriceBookOrders = []
      this.selectedPriceBookOrders = []
      analytics.track(MASTER_PRICE_BOOK.MASTER_PRICE_BOOK_IMPORTED_SUCCESSFULLY, { importedFile: fileLink, collection: this.collection })
    },

    // --- Price book order selection ---
    togglePriceBookOrder(orderId) {
      const idx = this.selectedPriceBookOrders.indexOf(orderId)
      if (idx > -1) {
        this.selectedPriceBookOrders.splice(idx, 1)
      } else {
        this.selectedPriceBookOrders.push(orderId)
      }
    },
    toggleSelectAllPriceBookOrders() {
      if (this.isSelectedPriceBookOrders) {
        this.selectedPriceBookOrders = []
      } else {
        const allOrderIds = this.allPriceBookOrders.map(order => order._id)
        const currentSelected = new Set(this.selectedPriceBookOrders)
        allOrderIds.forEach(id => currentSelected.add(id))
        this.selectedPriceBookOrders = Array.from(currentSelected)
      }
    },
    onSearchPriceBookOrders(searchString = '') {
      if (!searchString) {
        this.priceBookOrderList = [...this.allPriceBookOrders]
        return
      }
      const searchRegex = new RegExp(searchString, 'i')
      this.priceBookOrderList = this.allPriceBookOrders.filter(order => {
        const seqId = (order.orderSeqId || order.seqId || '').toString()
        const retailerName = (order.retailerName || '').toString()
        return searchRegex.test(seqId) || searchRegex.test(retailerName)
      })
    },
    closePriceBookOrderModal() {
      this.showOrderSelectionStep = false
      this.priceBookOrderList = []
      this.allPriceBookOrders = []
      this.selectedPriceBookOrders = []
      this.uploadedPriceBookFileLink = null
    },

    // --- Close ---
    onClickClosePriceBook() {
      if (this.isImportingPriceBook) {
        this.onClickBackPriceBook()
        return
      }
      this.resetState()
      this.$emit('close')
    },
    resetState() {
      this.selectedPriceBook = null
      this.isUploading = false
      this.isCreating = false
      this.priceBookName = ''
      this.isImportingPriceBook = false
      this.priceBookFile = null
      this.selectedCurrency = null
      this.showCurrencyDropdown = false
      this.showOrderSelectionStep = false
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-price-books {
  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &__create-form {
    padding: var(--v2-space-4);
    background: var(--v2-muted);
    border-radius: var(--v2-radius-md);
    margin-bottom: var(--v2-space-4);
  }

  &__currency-select {
    position: relative;
  }

  &__currency-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-background);
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
    }
  }

  &__currency-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    margin-top: var(--v2-space-1);
    background: var(--v2-card);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-shadow-lg);
    max-height: 240px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__currency-search {
    padding: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__currency-empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__currency-list {
    overflow-y: auto;
    max-height: 180px;
  }

  &__currency-item {
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--active {
      background: var(--v2-primary);
      color: var(--v2-primary-foreground);

      &:hover {
        background: var(--v2-primary);
      }
    }
  }

  &__section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
    margin-bottom: var(--v2-space-2);
  }

  &__list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);

    &:last-child {
      border-bottom: none;
    }
  }

  &__item-info {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    flex: 1;
    min-width: 0;
  }

  &__item-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    max-width: 180px;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
      color: var(--v2-foreground);
    }

    &--danger:hover {
      color: var(--v2-destructive);
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__rename-box {
    position: relative;
    width: 250px;

    ::v-deep .v2-input-wrapper {
      width: 100%;
    }

    ::v-deep .v2-input {
      width: 100%;
      padding-right: 60px;
      text-overflow: ellipsis;
    }
  }

  &__rename-actions {
    position: absolute;
    top: 19px;
    right: var(--v2-space-2);
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    pointer-events: auto;
  }

  &__rename-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: var(--v2-radius-sm);

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8);
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
    text-align: center;

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &--dragging {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
      cursor: copy;
    }
  }

  &__upload-icon {
    color: var(--v2-primary);
  }

  &__file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__file-icon {
    color: var(--v2-success);
  }

  &__remove-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-destructive);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__search-wrapper {
    flex: 1;
  }

  &__orders-header {
    display: flex;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: var(--v2-muted);
    border-radius: var(--v2-radius-md);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: var(--v2-space-1);
  }

  &__orders-col {
    flex: 1;
  }

  &__orders-list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__order-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
    cursor: pointer;
    transition: background 0.1s ease;
    font-size: 0.875rem;

    &:hover {
      background: var(--v2-muted);
    }

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
